import { actionMessages } from '@/components/shared/shared.messages';
import { backOfficeMessages } from '@/views/gallery/back-office-messages';
import { type NeonDialogMessage, NeonDialogService, NeonFunctionalColor } from '@art-basel/artbasel-lib-neon';
import { computed, type ComputedRef, onBeforeUnmount, onMounted, type Ref } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { string } from 'yup';

export const getRoutePathWithId = (route: string, id?: string): string => {
  return id ? route.replace(':id', id) : route;
};

// TODO: consolidate with NeonHttpUtils.encodeObjectToHttpQueryParams is HttpUtils
export const getRoutePathWithQueryParams = (route: string, params: Record<string, string>) => {
  try {
    // If the route starts with a slash, we need to add a random domain to the URL to create a URL object
    const domain = route.startsWith('/') ? 'https://arcual.com' : route;
    const url = new URL(`${domain}${route}`);

    // Add the query params to the URL
    Object.keys(params).forEach((key) => {
      url.searchParams.set(key, params[key]);
    });

    return `${route}${url.search}`;
  } catch (e) {
    console.error(e);
    return route;
  }
};

export const getMailUrl = (email?: string | null): string => {
  const isValid = string().required().email().isValidSync(email);

  return isValid ? `mailto:${email}` : '';
};

export const getTelephoneUrl = (phonenumber?: string | number | null): string => {
  return phonenumber ? `tel:${phonenumber}` : '';
};

/* c8 ignore start */
export function useConfirmNavigation(
  shouldConfirm: Ref<boolean> | ComputedRef<boolean> | (() => boolean),
  options?: Partial<Omit<NeonDialogMessage, 'open'>>,
) {
  const confirmMessage = backOfficeMessages.leaveDialogMessage;

  const confirmNavigation = async () => {
    return NeonDialogService.show({
      title: backOfficeMessages.leaveDialogTitle,
      question: backOfficeMessages.leaveDialogMessage,
      color: NeonFunctionalColor.Error,
      confirmLabel: actionMessages.leave,
      cancelLabel: actionMessages.cancel,
      ...(options || {}),
    });
  };

  const shouldConfirmValue = computed(() => {
    return typeof shouldConfirm === 'function' ? shouldConfirm() : (shouldConfirm as Ref).value;
  });

  // Handle browser actions (refresh, close tab, etc.)
  const handleBeforeUnload = async (event: Event) => {
    if (shouldConfirmValue.value) {
      event.preventDefault();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      event.returnValue = confirmMessage; // Required for backwards compatibility with some browsers
      return confirmMessage;
    }
  };

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  });

  onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
  });

  const route = useRoute();
  // handle null routes in jest test
  if (route?.matched?.length)
    onBeforeRouteLeave(async (to, from, next) => {
      if (shouldConfirmValue.value) {
        if (await confirmNavigation()) {
          return next();
        } else {
          return next(false);
        }
      } else {
        return next();
      }
    });

  return;
  /* c8 ignore end */
}
